// ===== Feedback.jsx =====
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import api from "../hooks/useApi";

const DEMO_FEEDBACK = [
  {
    _id: "f1",
    name: "Suresh Deshmukh",
    location: "Nandurbar",
    rating: 5,
    text: "Hitesh bhai and the team built our dream home exactly as we imagined. Quality is excellent, timeline was perfect. Highly recommend NirmalRam!",
  },
  {
    _id: "f2",
    name: "Priya Jain",
    location: "Shahada",
    rating: 5,
    text: "Very professional team. They suggested the best materials within our budget and the finishing work is outstanding. Our home looks beautiful!",
  },
  {
    _id: "f3",
    name: "Manoj Sonawane",
    location: "Nandurbar",
    rating: 5,
    text: "Government school project was completed before deadline. Great quality, honest billing. Will definitely work with them again.",
  },
  {
    _id: "f4",
    name: "Kavita Patil",
    location: "Nandurbar",
    rating: 5,
    text: "We got our 3BHK built by NirmalRam and we are thrilled. The team was disciplined, clean, and respectful. Rambhau sir visits personally to check quality.",
  },
];

export function Feedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [form, setForm] = useState({
    name: "",
    location: "",
    rating: 5,
    text: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await api.getFeedback();

        console.log("FEEDBACK API:", res);

        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

        if (data.length > 0) {
          setFeedbacks(data);
        } else {
          console.warn("Empty feedback, using demo");
          setFeedbacks(DEMO_FEEDBACK);
        }
      } catch (err) {
        console.error("FEEDBACK ERROR:", err?.response || err.message);
        setFeedbacks(DEMO_FEEDBACK);
      }
    };

    fetchFeedback();
  }, []);

  const handleSubmit = async () => {
    if (!form.name || !form.text)
      return toast.error("Please fill your name and review.");
    setSubmitting(true);
    try {
      await api.submitFeedback(form);
      toast.success("Thank you! Your review will appear after approval.");
      setForm({ name: "", location: "", rating: 5, text: "" });
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Client Reviews — NirmalRam Construction</title>
      </Helmet>

      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-tag">Client Reviews</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              What Our
              <br />
              Homeowners Say
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              Real feedback from real families who trusted us with their dream
              homes.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
              gap: "1.5rem",
            }}
          >
            {feedbacks.map((f, i) => (
              <motion.div
                key={f._id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.12)",
                  padding: "2rem",
                  borderRadius: "3px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "5rem",
                    color: "rgba(201,168,76,0.08)",
                    position: "absolute",
                    top: "0.5rem",
                    right: "1rem",
                    lineHeight: 1,
                  }}
                >
                  "
                </div>
                <div
                  style={{
                    color: "#C9A84C",
                    fontSize: "0.9rem",
                    marginBottom: "1rem",
                  }}
                >
                  {"★".repeat(f.rating)}
                </div>
                <p
                  style={{
                    color: "#8A8070",
                    fontSize: "0.95rem",
                    lineHeight: 1.8,
                    fontStyle: "italic",
                    marginBottom: "1.5rem",
                  }}
                >
                  {f.text}
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      background: "rgba(201,168,76,0.12)",
                      border: "1px solid rgba(201,168,76,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      color: "#C9A84C",
                      fontSize: "1rem",
                    }}
                  >
                    {f.name.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: "0.95rem" }}>
                      {f.name}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#5A5450" }}>
                      📍 {f.location}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit form */}
      <section
        style={{
          background: "#0A0A0A",
          borderTop: "1px solid rgba(201,168,76,0.1)",
          padding: "5rem 0",
        }}
      >
        <div className="container">
          <div style={{ maxWidth: "560px", margin: "0 auto" }}>
            <span className="section-tag">Share Your Experience</span>
            <h2
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "0.5rem",
              }}
            >
              Leave a Review
            </h2>
            <div className="section-divider" />
            <div
              style={{
                background: "#111",
                border: "1px solid rgba(201,168,76,0.15)",
                padding: "2.5rem",
                borderRadius: "3px",
              }}
            >
              <div className="form-row">
                <div className="form-group">
                  <label>Your Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Suresh Patil"
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    value={form.location}
                    onChange={(e) =>
                      setForm({ ...form, location: e.target.value })
                    }
                    placeholder="e.g. Nandurbar"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Rating</label>
                <select
                  value={form.rating}
                  onChange={(e) =>
                    setForm({ ...form, rating: Number(e.target.value) })
                  }
                >
                  <option value={5}>★★★★★ Excellent</option>
                  <option value={4}>★★★★ Very Good</option>
                  <option value={3}>★★★ Good</option>
                  <option value={2}>★★ Fair</option>
                  <option value={1}>★ Poor</option>
                </select>
              </div>
              <div className="form-group">
                <label>Your Review *</label>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="Share your experience with NirmalRam Construction..."
                />
              </div>
              <button
                className="btn-gold"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  opacity: submitting ? 0.7 : 1,
                }}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Review →"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Feedback;
